import React, { useEffect, useRef, useCallback, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toast } from 'react-toastify';
import styles from './index.module.css';
import EditorToolbar, { getModules, formats } from './EditorToolbar';
import DictationPanel from './DictationPanel';
import { sanitizePastedHtmlForQuill } from './journalUtils';

const JournalEditor = ({
    initialData,
    loadKey,
    holder = 'editorjs',
    onReadyApi = () => {},
    onChange = () => {},
    onBlur = () => {},
    isNewNote = false,
    handleEntryDeleteClick = () => {},
    handleRewriteWithAI = () => {},
    handleMoveEntry = () => {},
    sidebar = false,
    headerSlot = null,
}) => {
    const quillRef = useRef(null);
    const [editorReady, setEditorReady] = React.useState(false);
    const [editorContent, setEditorContent] = React.useState(initialData || '');
    const [dictationActive, setDictationActive] = useState(false);
    const toolbarId = `${holder}-toolbar`;
    const modules = getModules(toolbarId);
    const hasFormattedFirstLineRef = useRef(false);
    const lastInitialDataRef = useRef(null);
    const lastLoadKeyRef = useRef(loadKey);
    const isUserTypingRef = useRef(false);

    const syncEditorHtml = useCallback(
        html => {
            isUserTypingRef.current = true;
            setEditorContent(html);
            onChange(html);
            setTimeout(() => {
                isUserTypingRef.current = false;
            }, 100);
        },
        [onChange]
    );

    const appendDictationToEditor = useCallback(
        text => {
            const normalized = text?.trim();
            if (!normalized || !quillRef.current) {
                return;
            }

            const editor = quillRef.current.getEditor();
            if (!editor) {
                return;
            }

            const range = editor.getSelection(true);
            const index = typeof range?.index === 'number' ? range.index : Math.max(0, editor.getLength() - 1);
            const existing = editor.getText(0, index);
            const needsLeadingSpace = existing.trim().length > 0 && !/\s$/.test(existing);
            const insertion = `${needsLeadingSpace ? ' ' : ''}${normalized}`;

            editor.insertText(index, insertion, 'user');
            editor.setSelection(index + insertion.length, 0);
            syncEditorHtml(editor.root.innerHTML);
        },
        [syncEditorHtml]
    );

    const handleStartDictation = useCallback(() => {
        if (dictationActive) {
            return;
        }

        quillRef.current?.getEditor()?.focus();
        setDictationActive(true);
    }, [dictationActive]);

    const handleFinishDictation = useCallback(
        text => {
            appendDictationToEditor(text);
            setDictationActive(false);
        },
        [appendDictationToEditor]
    );

    const handleCancelDictation = useCallback(() => {
        setDictationActive(false);
    }, []);

    const handleDictationError = useCallback(message => {
        setDictationActive(false);
        toast.error(message || 'Voice dictation failed.');
    }, []);

    // Expose API when editor is ready
    useEffect(() => {
        if (editorReady && quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                onReadyApi({
                    save: async () => {
                        if (quillRef.current) {
                            const ed = quillRef.current.getEditor();
                            if (ed && ed.root) {
                                return ed.root.innerHTML;
                            }
                        }
                        return '';
                    },
                    focus: (insertText = '') => {
                        if (!quillRef.current) return;
                        const ed = quillRef.current.getEditor();
                        if (!ed) return;

                        ed.focus();
                        const extra = typeof insertText === 'string' ? insertText : '';
                        if (extra) {
                            const isEmpty = ed.getText().trim().length === 0;
                            const index = isEmpty ? 0 : Math.max(0, ed.getLength() - 1);
                            ed.insertText(index, extra, 'user');
                            ed.setSelection(index + extra.length, 0);
                            syncEditorHtml(ed.root.innerHTML);
                        } else {
                            const len = ed.getLength();
                            ed.setSelection(Math.max(0, len - 1), 0);
                        }
                    },
                    render: async data => {
                        if (!quillRef.current || data == null) return;
                        const ed = quillRef.current.getEditor();
                        if (!ed) return;

                        // Never set root.innerHTML directly — it breaks Quill's Scroll emitter.
                        const html = typeof data === 'string' ? data : '';
                        try {
                            const delta = ed.clipboard.convert({ html });
                            ed.setContents(delta, 'silent');
                        } catch {
                            ed.setText('');
                            if (html) {
                                ed.clipboard.dangerouslyPasteHTML(0, html, 'silent');
                            }
                        }

                        const nextHtml = ed.root.innerHTML;
                        setEditorContent(nextHtml);
                        lastInitialDataRef.current = nextHtml;
                    },
                    clear: async () => {
                        if (quillRef.current) {
                            const ed = quillRef.current.getEditor();
                            if (ed) {
                                ed.setContents([], 'silent');
                                setEditorContent('');
                                lastInitialDataRef.current = null;
                                hasFormattedFirstLineRef.current = false;
                            }
                        }
                    },
                });
            }
        }
    }, [editorReady, onReadyApi, syncEditorHtml]);

    // Track the last initialData to prevent unnecessary resets.
    // loadKey changes when switching notes — always apply, even if the user was typing.
    useEffect(() => {
        const noteSwitched = loadKey !== undefined && loadKey !== lastLoadKeyRef.current;
        if (noteSwitched) {
            lastLoadKeyRef.current = loadKey;
            isUserTypingRef.current = false;
        }

        const applyHtml = html => {
            if (quillRef.current) {
                const ed = quillRef.current.getEditor();
                if (ed) {
                    try {
                        const delta = ed.clipboard.convert({ html: html || '' });
                        ed.setContents(delta, 'silent');
                        const nextHtml = ed.root.innerHTML;
                        setEditorContent(nextHtml);
                        lastInitialDataRef.current = html;
                        return true;
                    } catch {
                        // fall through to controlled value update
                    }
                }
            }
            setEditorContent(html || '');
            lastInitialDataRef.current = html;
            return false;
        };

        if (initialData !== null && initialData !== undefined) {
            if (noteSwitched || (lastInitialDataRef.current !== initialData && !isUserTypingRef.current && !dictationActive)) {
                applyHtml(initialData);
            }
        } else if (noteSwitched || (!isUserTypingRef.current && !dictationActive)) {
            if (quillRef.current) {
                const ed = quillRef.current.getEditor();
                if (ed) {
                    ed.setContents([], 'silent');
                }
            }
            setEditorContent('');
            lastInitialDataRef.current = null;
        }
    }, [dictationActive, initialData, loadKey]);

    const handleChange = html => {
        // ReactQuill's onChange receives the HTML content directly as a string
        if (html !== null && html !== undefined) {
            // Update internal state immediately so typing works
            setEditorContent(html);
            // Mark that user is typing
            isUserTypingRef.current = true;
            // Notify parent component
            onChange(html);
            // Reset typing flag after a short delay to allow initialData updates when needed
            setTimeout(() => {
                isUserTypingRef.current = false;
            }, 100);
        }
    };

    const handleBlur = () => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                onBlur(editor.root.innerHTML);
            }
        }
    };

    // Check if editor is ready after mount
    React.useEffect(() => {
        const checkReady = () => {
            if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                if (editor && editor.root) {
                    setEditorReady(true);
                } else {
                    // Retry after a short delay
                    setTimeout(checkReady, 50);
                }
            }
        };
        checkReady();
    }, []);

    // Preserve Word/Docs headings & inline styles when pasting into the body
    useEffect(() => {
        if (!editorReady || !quillRef.current) return;

        const editor = quillRef.current.getEditor();
        if (!editor?.root) return;

        const handlePaste = e => {
            const html = e.clipboardData?.getData('text/html');
            if (!html?.trim()) return;

            const cleaned = sanitizePastedHtmlForQuill(html);
            if (!cleaned) return;

            e.preventDefault();
            e.stopPropagation();

            const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
            if (range.length > 0) {
                editor.deleteText(range.index, range.length, 'user');
            }

            editor.clipboard.dangerouslyPasteHTML(range.index, cleaned, 'user');
            syncEditorHtml(editor.root.innerHTML);
        };

        editor.root.addEventListener('paste', handlePaste, true);
        return () => editor.root.removeEventListener('paste', handlePaste, true);
    }, [editorReady, syncEditorHtml]);

    // Reset flag whenever isNewNote becomes true (when creating a new note)
    // Also reset when initialData is cleared (indicating a new note)
    useEffect(() => {
        if (isNewNote || initialData === null || initialData === '') {
            hasFormattedFirstLineRef.current = false;
        }
    }, [isNewNote, initialData]);

    // Set up keyboard handlers for new notes: first line h2 bold, subsequent lines regular
    useEffect(() => {
        if (!editorReady || !isNewNote || sidebar || !quillRef.current) {
            return;
        }

        const editor = quillRef.current.getEditor();
        if (!editor) return;

        // Ensure flag is reset for new note
        hasFormattedFirstLineRef.current = false;

        // Format first line as h2 bold when user starts typing
        const handleTextChange = (delta, oldDelta, source) => {
            if (!isNewNote || source !== 'user') return;

            // Check if editor is empty - if so, reset the flag
            const text = editor.getText();
            if (text.trim().length === 0) {
                hasFormattedFirstLineRef.current = false;
                return;
            }

            // If already formatted, skip
            if (hasFormattedFirstLineRef.current) return;

            const selection = editor.getSelection();
            if (!selection) return;

            // Check if we're at the first line by checking if selection is at the start
            // or if the line starts at index 0
            const [line, offset] = editor.getLine(selection.index);
            if (!line) return;

            // Check if we're on the first line (line starts at index 0 or selection is at start)
            const lineStart = selection.index - offset;
            const isFirstLine = lineStart === 0;

            if (isFirstLine && !hasFormattedFirstLineRef.current) {
                // Get the length of the first line
                const firstLineLength = line.length();
                const firstLineText = editor.getText(0, firstLineLength);
                if (firstLineText.trim().length > 0) {
                    // Format first line as h2 and bold
                    editor.formatLine(0, firstLineLength, 'header', 2);
                    editor.formatText(0, firstLineLength, 'bold', true);
                    hasFormattedFirstLineRef.current = true;
                }
            }
        };

        // Handle Enter key to format next line as regular text
        const handleEnter = range => {
            if (!isNewNote) return true; // Allow default behavior for existing notes

            // Clear formatting before Enter so the new line doesn't inherit it
            editor.format('header', false);
            editor.format('bold', false);

            // After Enter is pressed, format the new line as regular text
            // Use multiple timeouts to catch the new line at different stages
            setTimeout(() => {
                const newSelection = editor.getSelection();
                if (newSelection) {
                    const newIndex = newSelection.index;

                    // Get the new line/block that was just created
                    const [newLine, offset] = editor.getLine(newIndex);
                    if (newLine) {
                        const lineStart = newIndex - offset;
                        const lineLength = Math.max(newLine.length(), 1);

                        // Remove all formatting from the new line
                        editor.formatLine(lineStart, lineLength, 'header', false);
                        editor.formatText(lineStart, lineLength, 'bold', false);

                        // Also clear format at cursor position
                        editor.format('header', false);
                        editor.format('bold', false);
                    }
                }
            }, 10);

            // Second pass to ensure formatting is removed
            setTimeout(() => {
                const newSelection = editor.getSelection();
                if (newSelection) {
                    const newIndex = newSelection.index;
                    const [newLine, offset] = editor.getLine(newIndex);
                    if (newLine) {
                        const lineStart = newIndex - offset;
                        const format = editor.getFormat(lineStart);
                        if (format.header || format.bold) {
                            editor.formatLine(lineStart, 1, 'header', false);
                            editor.formatText(lineStart, 1, 'bold', false);
                            editor.format('header', false);
                            editor.format('bold', false);
                        }
                    }
                }
            }, 50);

            return true; // Allow default Enter behavior
        };

        // Track when we're on a new line (not the first line) to ensure it's normal text
        const handleSelectionChange = () => {
            if (!isNewNote || !hasFormattedFirstLineRef.current) return;

            const selection = editor.getSelection();
            if (!selection) return;

            const [line, offset] = editor.getLine(selection.index);
            if (!line) return;

            const lineStart = selection.index - offset;

            // If we're not on the first line (lineStart > 0), ensure it's normal text
            if (lineStart > 0) {
                const format = editor.getFormat(selection.index);
                if (format.header || format.bold) {
                    const lineLength = Math.max(line.length(), 1);
                    editor.formatLine(lineStart, lineLength, 'header', false);
                    editor.formatText(lineStart, lineLength, 'bold', false);
                    editor.format('header', false);
                    editor.format('bold', false);
                }
            }
        };

        // Listen for text changes
        editor.on('text-change', handleTextChange);

        // Listen for selection changes to catch when user moves to a new line
        editor.on('selection-change', handleSelectionChange);

        // Add Enter key binding
        const keyboard = editor.getModule('keyboard');
        if (keyboard) {
            keyboard.addBinding({ key: 'Enter' }, handleEnter);
        }

        return () => {
            editor.off('text-change', handleTextChange);
            editor.off('selection-change', handleSelectionChange);
            // Reset flag on cleanup
            hasFormattedFirstLineRef.current = false;
        };
    }, [editorReady, isNewNote, sidebar]);

    return (
        <div id={holder} className={`${styles.editorHolder} ${sidebar ? '' : styles.editorHolderJournal}`}>
            <style>{`
                #${holder} .ql-container {
                    border: none !important;
                    border-radius: 0 !important;
                    margin-top: 0 !important;
                }
                #${holder} .ql-editor {
                    border: none !important;
                    padding: 0 !important;
                    min-height: 280px;
                    font-size: 15px;
                    line-height: 1.6;
                }
                #${holder} .ql-formats {
                    margin-left: 0 !important;
                }
                #${holder} .ql-toolbar {
                    text-align: start !important;
                    border: none !important;
                }
                #${holder} .ql-snow.ql-toolbar,
                #${holder} .ql-snow .ql-toolbar {
                    border: none !important;
                }
            `}</style>
            <div className={sidebar ? undefined : styles.editorToolbarWrap}>
                <EditorToolbar
                    sidebar={sidebar}
                    toolbarId={toolbarId}
                    handleEntryDeleteClick={handleEntryDeleteClick}
                    handleRewriteWithAI={handleRewriteWithAI}
                    handleMoveEntry={handleMoveEntry}
                    isDictating={dictationActive}
                    onToggleDictation={handleStartDictation}
                />
            </div>
            <div className={sidebar ? undefined : styles.editorDocument}>
                {headerSlot}
                <div className={sidebar ? undefined : styles.editorDocumentContent}>
                    <DictationPanel
                        active={dictationActive}
                        onFinish={handleFinishDictation}
                        onCancel={handleCancelDictation}
                        onError={handleDictationError}
                    />
                    <ReactQuill useSemanticHTML={false}
                        ref={quillRef}
                        theme="snow"
                        modules={modules}
                        formats={formats}
                        placeholder="Start writing..."
                        value={editorContent}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </div>
            </div>
        </div>
    );
};

export default JournalEditor;
